import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Target, 
  Award, AlertTriangle, RefreshCw, Download, Zap, Package
} from 'lucide-react';
import reportService from '../../services/reportService';

/**
 * 📊 PAGE DE REPORTING AVEC VRAIES DONNÉES
 * ✅ Conserve exactement la structure et design existants
 * ✅ Corrige toutes les erreurs de compilation
 * ✅ Intègre les vraies données basées sur les DTOs backend
 */
const ReportingPage = () => {
  // ===== UTILITAIRES DE FORMATAGE INTÉGRÉS =====
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toLocaleString('fr-FR');
  };

  const formatPercentage = (value, showSign = true) => {
    if (!value && value !== 0) return '0%';
    return `${showSign && value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // ===== ÉTATS =====
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // ✅ DONNÉES RÉELLES basées sur les DTOs backend
  const [reportData, setReportData] = useState({
    // SaleStatsDto mapping
    salesStats: {
      totalRevenue: 0,        // total_revenue (BigDecimal)
      totalSales: 0,          // sale_count (Long) 
      totalCustomers: 0,      // calculé depuis les ventes
      averageOrderValue: 0,   // average_sale (BigDecimal)
      revenueGrowth: 0,       // calculé
      salesGrowth: 0,         // calculé
      customerGrowth: 0       // calculé
    },
    // Données de revenus pour graphiques
    revenueData: [],
    // ProductStatsDto mapping
    topProducts: [],          // product_name, total_sold, total_revenue
    // Top clients extraits des ventes
    topCustomers: [],         // customer_name, order_count, total_purchases
    // Modes de paiement extraits des ventes
    paymentMethods: [],       // PaymentMethod enum
    // Performance équipe basée sur EmployeeStatsDto
    teamPerformance: [],      // seller stats avec targets
    // StockDto mapping pour alertes
    stockAlerts: []           // product_name, current_stock, min_threshold, alert_level
  });

  // ===== CHARGEMENT DES VRAIES DONNÉES =====
  const loadReportData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Chargement des vraies données...');

      // Exécuter toutes les requêtes en parallèle avec Promise.allSettled pour éviter les crashes
      const results = await Promise.allSettled([
        reportService.getSalesStats(),
        reportService.getTopProducts({ limit: 10 }),
        reportService.getStockAlerts({ limit: 50 }),
        reportService.getTopCustomers({ limit: 10 }),
        reportService.getRevenueData(),
        reportService.getPaymentMethodsStats(),
        reportService.getTeamPerformance()
      ]);

      const [
        salesStatsResult,
        topProductsResult,
        stockAlertsResult,
        topCustomersResult,
        revenueResult,
        paymentResult,
        teamResult
      ] = results;

      // ✅ MAPPER LES DONNÉES selon les DTOs avec fallbacks sécurisés
      const mappedData = {
        // SaleStatsDto -> salesStats
        salesStats: salesStatsResult.status === 'fulfilled' && salesStatsResult.value.success ? {
          totalRevenue: salesStatsResult.value.data.total_revenue || 
                       salesStatsResult.value.data.totalRevenue || 0,
          totalSales: salesStatsResult.value.data.sale_count || 
                     salesStatsResult.value.data.totalSales || 0,
          totalCustomers: salesStatsResult.value.data.unique_customers || 
                         salesStatsResult.value.data.totalCustomers || 0,
          averageOrderValue: salesStatsResult.value.data.average_sale || 
                            salesStatsResult.value.data.averageOrderValue || 0,
          revenueGrowth: salesStatsResult.value.data.revenue_growth || 0,
          salesGrowth: salesStatsResult.value.data.sales_growth || 0,
          customerGrowth: salesStatsResult.value.data.customer_growth || 0
        } : {
          totalRevenue: 0,
          totalSales: 0,
          totalCustomers: 0,
          averageOrderValue: 0,
          revenueGrowth: 0,
          salesGrowth: 0,
          customerGrowth: 0
        },

        // ProductStatsDto[] -> topProducts  
        topProducts: topProductsResult.status === 'fulfilled' && topProductsResult.value.success ? 
          topProductsResult.value.data.map(product => ({
            productId: product.product_id || product.productId,
            productName: product.product_name || product.productName || 'Produit sans nom',
            categoryName: product.category_name || product.categoryName || 'N/A',
            totalSold: product.total_sold || product.totalSold || 0,
            totalRevenue: product.total_revenue || product.totalRevenue || 0,
            saleCount: product.sale_count || product.saleCount || 0,
            currentStock: product.current_stock || product.currentStock || 0,
            todaySold: product.today_sold || product.todaySold || 0
          })) : [],

        // StockDto[] -> stockAlerts
        stockAlerts: stockAlertsResult.status === 'fulfilled' && stockAlertsResult.value.success ? 
          stockAlertsResult.value.data.map(alert => ({
            productId: alert.product_id || alert.productId,
            productName: alert.product_name || alert.productName || 'Produit sans nom',
            categoryName: alert.category_name || alert.categoryName || 'N/A',
            currentStock: alert.current_stock || alert.currentStock || 0,
            minThreshold: alert.min_threshold || alert.minThreshold || 0,
            maxThreshold: alert.max_threshold || alert.maxThreshold || 0,
            status: alert.alert_level || alert.status || 'LOW'
          })) : [],

        // Top clients mappés
        topCustomers: topCustomersResult.status === 'fulfilled' && topCustomersResult.value.success ? 
          topCustomersResult.value.data.map(customer => ({
            customerId: customer.customer_id || customer.customerId,
            customerName: customer.customer_name || customer.customerName || 'Client anonyme',
            orderCount: customer.order_count || customer.orderCount || 0,
            totalPurchases: customer.total_amount || customer.totalPurchases || customer.totalAmount || 0,
            averageOrderValue: customer.average_order_value || customer.averageOrderValue || 0,
            lastPurchase: customer.last_purchase_date || customer.lastPurchase
          })) : [],

        // Données de revenus pour graphiques
        revenueData: revenueResult.status === 'fulfilled' && revenueResult.value.success ? 
          revenueResult.value.data.map(item => ({
            date: item.date,
            revenue: item.revenue || 0,
            sales: item.sales_count || item.salesCount || 0,
            averageOrder: item.avg_order_value || item.averageOrderValue || 0
          })) : [],

        // PaymentMethod stats
        paymentMethods: paymentResult.status === 'fulfilled' && paymentResult.value.success ? 
          paymentResult.value.data.map(method => ({
            method: method.payment_method || method.method,
            methodLabel: method.method_label || method.methodLabel || method.method,
            amount: method.total_amount || method.amount || 0,
            count: method.transaction_count || method.count || 0,
            percentage: method.percentage || 0
          })) : [],

        // EmployeeStatsDto[] -> teamPerformance
        teamPerformance: teamResult.status === 'fulfilled' && teamResult.value.success ? 
          teamResult.value.data.map(member => ({
            employeeId: member.employee_id || member.employeeId,
            sellerName: member.employee_name || member.sellerName || member.employeeName || 'Employé',
            sales: member.total_sales || member.sales || member.salesCount || 0,
            revenue: member.total_revenue || member.revenue || 0,
            target: member.target || member.monthly_target || 50000,
            ranking: member.ranking || 0,
            achievement: member.achievement_percent || member.achievement || 0,
            progressPercent: member.progress_percent || member.progressPercent || 0
          })) : []
      };

      setReportData(mappedData);
      console.log('✅ Données mappées selon DTOs:', mappedData);

    } catch (err) {
      console.error('❌ Erreur chargement rapport:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []); // Dépendances vides pour useCallback

  // Charger les données au montage
  useEffect(() => {
    loadReportData();
  }, [loadReportData]); // ✅ Corrigé: dépendance ajoutée

  // ===== COMPOSANTS =====

  // Cartes de statistiques principales
  const StatsCards = () => {
    const stats = [
      {
        title: 'Chiffre d\'Affaires',
        value: formatCurrency(reportData.salesStats.totalRevenue),
        growth: reportData.salesStats.revenueGrowth,
        icon: DollarSign,
        color: '#3B82F6'
      },
      {
        title: 'Nombre de Ventes',
        value: formatNumber(reportData.salesStats.totalSales),
        growth: reportData.salesStats.salesGrowth,
        icon: ShoppingCart,
        color: '#10B981'
      },
      {
        title: 'Clients Actifs',
        value: formatNumber(reportData.salesStats.totalCustomers),
        growth: reportData.salesStats.customerGrowth,
        icon: Users,
        color: '#F59E0B'
      },
      {
        title: 'Panier Moyen',
        value: formatCurrency(reportData.salesStats.averageOrderValue),
        growth: null,
        icon: Target,
        color: '#06B6D4'
      }
    ];

    return (
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          const GrowthIcon = stat.growth ? (stat.growth >= 0 ? TrendingUp : TrendingDown) : null;

          return (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <div className="stat-info">
                  <h6 className="stat-title">{stat.title}</h6>
                  <div className="stat-value">{stat.value}</div>
                  {stat.growth !== null && (
                    <div className={`stat-growth ${stat.growth >= 0 ? 'positive' : 'negative'}`}>
                      {GrowthIcon && <GrowthIcon size={16} />}
                      <span>{formatPercentage(stat.growth)}</span>
                    </div>
                  )}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${stat.color}20` }}>
                  <IconComponent size={32} style={{ color: stat.color }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Graphiques de revenus
  const RevenueChart = () => (
    <div className="chart-card">
      <div className="chart-header">
        <h6>
          <TrendingUp size={18} style={{ color: '#3B82F6' }} />
          Évolution des Revenus
        </h6>
      </div>
      <div className="chart-content">
        {reportData.revenueData.length > 0 ? (
          <div className="simple-chart">
            <p>📊 {reportData.revenueData.length} points de données sur la période</p>
            <div className="chart-summary">
              <div className="summary-item">
                <span className="label">Total période:</span>
                <span className="value">
                  {formatCurrency(reportData.revenueData.reduce((sum, item) => sum + item.revenue, 0))}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Ventes totales:</span>
                <span className="value">
                  {formatNumber(reportData.revenueData.reduce((sum, item) => sum + item.sales, 0))}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <TrendingUp size={48} />
            <p>Données de revenus en cours de chargement</p>
          </div>
        )}
      </div>
    </div>
  );

  // Graphique des modes de paiement
  const PaymentChart = () => (
    <div className="chart-card">
      <div className="chart-header">
        <h6>
          <DollarSign size={18} style={{ color: '#10B981' }} />
          Modes de Paiement
        </h6>
      </div>
      <div className="chart-content">
        {reportData.paymentMethods.length > 0 ? (
          <div className="payment-breakdown">
            {reportData.paymentMethods.map((method, index) => (
              <div key={index} className="payment-item">
                <div className="payment-info">
                  <span className="method-name">{method.methodLabel}</span>
                  <div className="method-details">
                    <span>{method.count} transactions</span>
                    <span>{formatCurrency(method.amount)}</span>
                  </div>
                </div>
                <div className="method-percentage">
                  {method.percentage.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <DollarSign size={48} />
            <p>Aucune donnée de paiement</p>
          </div>
        )}
      </div>
    </div>
  );

  // ✅ SECTION PRODUITS COMPLÈTE
  const ProductsTab = () => (
    <div className="products-content">
      <div className="products-grid">
        {/* Top 5 Produits les plus vendus */}
        <div className="table-card">
          <div className="table-header">
            <h6>
              <Award size={18} style={{ color: '#F59E0B' }} />
              Top 5 Produits - Meilleures Ventes
            </h6>
          </div>
          <div className="table-content">
            {reportData.topProducts.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Produit</th>
                    <th>Catégorie</th>
                    <th>Vendus</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topProducts.slice(0, 5).map((product, index) => (
                    <tr key={product.productId || index}>
                      <td className="rank">{index + 1}</td>
                      <td className="product-name">{product.productName}</td>
                      <td>
                        <span className="category-badge">{product.categoryName}</span>
                      </td>
                      <td>
                        <span className="quantity-badge">{formatNumber(product.totalSold)}</span>
                      </td>
                      <td>
                        <span className={`stock-badge ${product.currentStock <= 10 ? 'low' : 'normal'}`}>
                          {product.currentStock}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Award size={32} />
                <p>Aucune donnée de vente</p>
              </div>
            )}
          </div>
        </div>

        {/* Top 5 Produits - Meilleurs Revenus */}
        <div className="table-card">
          <div className="table-header">
            <h6>
              <DollarSign size={18} style={{ color: '#10B981' }} />
              Top 5 Produits - Meilleurs Revenus
            </h6>
          </div>
          <div className="table-content">
            {reportData.topProducts.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Produit</th>
                    <th>Revenus</th>
                    <th>Ventes</th>
                    <th>Prix Moy.</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topProducts
                    .sort((a, b) => b.totalRevenue - a.totalRevenue)
                    .slice(0, 5)
                    .map((product, index) => (
                    <tr key={product.productId || index}>
                      <td className="rank">{index + 1}</td>
                      <td className="product-name">{product.productName}</td>
                      <td className="revenue">{formatCurrency(product.totalRevenue)}</td>
                      <td>
                        <span className="sales-badge">{formatNumber(product.saleCount)}</span>
                      </td>
                      <td>
                        {formatCurrency(product.totalSold > 0 ? product.totalRevenue / product.totalSold : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <DollarSign size={32} />
                <p>Aucune donnée de revenus</p>
              </div>
            )}
          </div>
        </div>

        {/* Performance Produits Aujourd'hui */}
        <div className="table-card full-width">
          <div className="table-header">
            <h6>
              <Package size={18} style={{ color: '#06B6D4' }} />
              Performance Produits - Aujourd'hui
            </h6>
          </div>
          <div className="table-content">
            {reportData.topProducts.filter(p => p.todaySold > 0).length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Catégorie</th>
                    <th>Vendus Aujourd'hui</th>
                    <th>Total Vendus</th>
                    <th>Stock Actuel</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topProducts
                    .filter(p => p.todaySold > 0)
                    .sort((a, b) => b.todaySold - a.todaySold)
                    .slice(0, 10)
                    .map((product, index) => (
                    <tr key={product.productId || index}>
                      <td className="product-name">{product.productName}</td>
                      <td>
                        <span className="category-badge">{product.categoryName}</span>
                      </td>
                      <td>
                        <span className="today-badge">{product.todaySold}</span>
                      </td>
                      <td>{formatNumber(product.totalSold)}</td>
                      <td>
                        <span className={`stock-badge ${product.currentStock <= 10 ? 'low' : 'normal'}`}>
                          {product.currentStock}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${product.currentStock > 10 ? 'success' : product.currentStock > 0 ? 'warning' : 'danger'}`}>
                          {product.currentStock > 10 ? 'Bon Stock' : product.currentStock > 0 ? 'Stock Faible' : 'Rupture'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Package size={32} />
                <p>Aucune vente aujourd'hui</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Onglet Dashboard
  const DashboardTab = () => (
    <div className="dashboard-content">
      {/* <StatsCards /> */}

      <div className="charts-row">
        <RevenueChart />
        <PaymentChart />
      </div>

      <div className="tables-row">
        <div className="table-card">
          <div className="table-header">
            <h6>
              <Award size={18} style={{ color: '#F59E0B' }} />
              Top Produits
            </h6>
          </div>
          <div className="table-content">
            {reportData.topProducts.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Produit</th>
                    <th>Vendus</th>
                    <th>Revenus</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topProducts.slice(0, 5).map((product, index) => (
                    <tr key={product.productId || index}>
                      <td className="rank">{index + 1}</td>
                      <td className="product-name">{product.productName}</td>
                      <td>
                        <span className="quantity-badge">{formatNumber(product.totalSold)}</span>
                      </td>
                      <td className="revenue">{formatCurrency(product.totalRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Award size={32} />
                <p>Aucune donnée de vente</p>
              </div>
            )}
          </div>
        </div>

        <div className="table-card">
          <div className="table-header">
            <h6>
              <Users size={18} style={{ color: '#06B6D4' }} />
              Top Clients
            </h6>
          </div>
          <div className="table-content">
            {reportData.topCustomers.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Client</th>
                    <th>Cmd</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topCustomers.map((customer, index) => (
                    <tr key={customer.customerId || index}>
                      <td className="rank">{index + 1}</td>
                      <td className="customer-name">{customer.customerName}</td>
                      <td>
                        <span className="order-badge">{customer.orderCount}</span>
                      </td>
                      <td className="revenue">{formatCurrency(customer.totalPurchases)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Users size={32} />
                <p>Aucun client enregistré</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {reportData.stockAlerts.length > 0 && (
        <div className="alerts-card">
          <div className="alert-header">
            <h6>
              <AlertTriangle size={18} style={{ color: '#F59E0B' }} />
              Alertes Stock ({reportData.stockAlerts.length})
            </h6>
          </div>
          <div className="alerts-grid">
            {reportData.stockAlerts.slice(0, 6).map((alert, index) => (
              <div key={alert.productId || index} className="alert-item">
                <div className="alert-product">{alert.productName}</div>
                <div className="alert-info">
                  Catégorie: {alert.categoryName} • Stock: {alert.currentStock}/{alert.minThreshold}
                </div>
                <span className={`alert-badge ${alert.status === 'CRITICAL' ? 'critical' : 'low'}`}>
                  {alert.status === 'CRITICAL' ? 'Critique' : 'Faible'}
                </span>
              </div>
            ))}
          </div>
          {reportData.stockAlerts.length > 6 && (
            <div className="alert-footer">
              <button 
                className="btn-outline-warning"
                onClick={() => setActiveTab('stock')}
              >
                Voir toutes les alertes ({reportData.stockAlerts.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Onglet Équipe
  const TeamTab = () => (
    <div className="team-content">
      <div className="team-grid">
        {reportData.teamPerformance.length > 0 ? reportData.teamPerformance.map((seller, index) => (
          <div key={seller.employeeId || index} className="team-card">
            <div className="team-header">
              <h6>
                <Zap size={18} style={{ color: '#3B82F6' }} />
                {seller.sellerName}
              </h6>
              <span className={`status-badge ${seller.revenue >= seller.target ? 'success' : 'warning'}`}>
                {seller.revenue >= seller.target ? 'Objectif atteint' : 'En cours'}
              </span>
            </div>

            <div className="team-stats">
              <div className="stat-item">
                <div className="stat-number">{seller.sales}</div>
                <div className="stat-label">Ventes</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{formatCurrency(seller.revenue)}</div>
                <div className="stat-label">CA Réalisé</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{formatCurrency(seller.target)}</div>
                <div className="stat-label">Objectif</div>
              </div>
            </div>

            <div className="progress-section">
              <div className="progress-info">
                <span>Progression</span>
                <span>{((seller.revenue / seller.target) * 100).toFixed(1)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${seller.revenue >= seller.target ? 'success' : 'primary'}`}
                  style={{ width: `${Math.min((seller.revenue / seller.target) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )) : (
          <div className="empty-state">
            <Zap size={48} />
            <p>Aucune donnée d'équipe disponible</p>
          </div>
        )}
      </div>
    </div>
  );

  // Onglet Stock
  const StockTab = () => (
    <div className="stock-content">
      <div className="stock-table-card">
        <div className="table-header">
          <h6>
            <AlertTriangle size={18} style={{ color: '#F59E0B' }} />
            Alertes Stock ({reportData.stockAlerts.length})
          </h6>
        </div>
        <div className="table-content">
          {reportData.stockAlerts.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle size={48} />
              <p>Aucune alerte stock pour le moment</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Stock Actuel</th>
                  <th>Seuil Min</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.stockAlerts.map((alert, index) => (
                  <tr key={alert.productId || index}>
                    <td className="product-name">{alert.productName}</td>
                    <td>
                      <span className="category-badge">{alert.categoryName}</span>
                    </td>
                    <td>
                      <span className={`stock-value ${
                        alert.currentStock === 0 ? 'danger' : 
                        alert.currentStock <= alert.minThreshold ? 'warning' : 'success'
                      }`}>
                        {alert.currentStock}
                      </span>
                    </td>
                    <td className="threshold">{alert.minThreshold}</td>
                    <td>
                      <span className={`status-badge ${alert.status === 'CRITICAL' ? 'critical' : 'low'}`}>
                        {alert.status === 'CRITICAL' ? 'Critique' : 'Faible'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );

  // ===== RENDU PRINCIPAL =====
  return (
    <div className="reporting-page">
      <style>{`
        .reporting-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Header */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-title h3 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 700;
          color: #1a202c;
        }

        .page-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 0.875rem;
        }

        .page-actions {
          display: flex;
          gap: 0.5rem;
        }

        /* Boutons */
        button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #3B82F6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563EB;
        }

        .btn-outline-primary {
          background: transparent;
          color: #3B82F6;
          border: 1px solid #3B82F6;
        }

        .btn-outline-primary:hover {
          background: #3B82F6;
          color: white;
        }

        .btn-outline-warning {
          background: transparent;
          color: #F59E0B;
          border: 1px solid #F59E0B;
        }

        .btn-outline-warning:hover {
          background: #F59E0B;
          color: white;
        }

        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Spinner */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          color: #64748b;
        }

        .loading-spinner {
          width: 2rem;
          height: 2rem;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #3B82F6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Alertes */
        .alert {
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .alert-success {
          background: #D1FAE5;
          color: #065F46;
          border: 1px solid #A7F3D0;
        }

        .alert-danger {
          background: #FEE2E2;
          color: #991B1B;
          border: 1px solid #FECACA;
        }

        .alert-close {
          background: none;
          border: none;
          color: currentColor;
          padding: 0;
          font-size: 1.25rem;
          cursor: pointer;
        }

        /* Onglets */
        .tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          border-radius: 0.5rem 0.5rem 0 0;
          color: #6b7280;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab.active {
          background: #3B82F6;
          color: white;
        }

        .tab:not(.active):hover {
          background: #f3f4f6;
          color: #374151;
        }

        .tab-badge {
          background: #EF4444;
          color: white;
          padding: 0.125rem 0.375rem;
          border-radius: 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 0.25rem;
        }

        /* Cartes de statistiques */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .stat-info {
          flex: 1;
        }

        .stat-title {
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
          margin: 0 0 0.5rem 0;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 0.5rem 0;
        }

        .stat-growth {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .stat-growth.positive {
          color: #059669;
        }

        .stat-growth.negative {
          color: #DC2626;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Graphiques */
        .charts-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .chart-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .chart-header {
          padding: 1.5rem 1.5rem 0;
        }

        .chart-header h6 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0 0 1rem 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .chart-content {
          padding: 0 1.5rem 1.5rem;
        }

        .simple-chart {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
        }

        .chart-summary {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1rem;
          text-align: left;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .summary-item .label {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .summary-item .value {
          font-size: 1rem;
          font-weight: 600;
          color: #1a202c;
        }

        .payment-breakdown {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .payment-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 0.5rem;
        }

        .payment-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .method-name {
          font-weight: 500;
          color: #1a202c;
        }

        .method-details {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .method-percentage {
          font-weight: 600;
          color: #3B82F6;
        }

        /* Tableaux */
        .tables-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .table-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .table-header {
          padding: 1.5rem 1.5rem 0;
        }

        .table-header h6 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0 0 1rem 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .table-content {
          padding: 0 1.5rem 1.5rem;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th {
          text-align: left;
          padding: 0.75rem 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
        }

        td {
          padding: 0.75rem 0.5rem;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.875rem;
        }

        .rank {
          width: 40px;
          text-align: center;
          color: #6b7280;
          font-weight: 600;
        }

        .product-name, .customer-name {
          font-weight: 500;
          color: #1a202c;
        }

        .revenue {
          font-weight: 600;
          color: #059669;
        }

        /* Badges */
        .quantity-badge, .order-badge, .sales-badge, .today-badge {
          background: #EBF8FF;
          color: #1E40AF;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .category-badge {
          background: #F0F9FF;
          color: #0369A1;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .stock-badge.normal {
          background: #D1FAE5;
          color: #065F46;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .stock-badge.low {
          background: #FEF3C7;
          color: #92400E;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge.success {
          background: #D1FAE5;
          color: #065F46;
        }

        .status-badge.warning {
          background: #FEF3C7;
          color: #92400E;
        }

        .status-badge.danger {
          background: #FEE2E2;
          color: #991B1B;
        }

        .status-badge.critical {
          background: #FEE2E2;
          color: #991B1B;
        }

        .status-badge.low {
          background: #FEF3C7;
          color: #92400E;
        }

        /* Section Produits */
        .products-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .products-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        /* Alertes Stock */
        .alerts-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .alert-header {
          background: rgba(245, 158, 11, 0.1);
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          border-radius: 0.75rem 0.75rem 0 0;
        }

        .alert-header h6 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #92400e;
        }

        .alerts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          padding: 1.5rem;
        }

        .alert-item {
          padding: 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: #fafafa;
        }

        .alert-product {
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .alert-info {
          color: #6b7280;
          font-size: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .alert-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .alert-badge.critical {
          background: #FEE2E2;
          color: #991B1B;
        }

        .alert-badge.low {
          background: #FEF3C7;
          color: #92400E;
        }

        .alert-footer {
          text-align: center;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        /* Équipe */
        .team-content {
          display: flex;
          flex-direction: column;
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }

        .team-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .team-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .team-header h6 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .team-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .stat-item {
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 0.5rem;
        }

        .stat-number {
          font-weight: 700;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
        }

        .progress-section {
          margin-top: 1rem;
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .progress-bar {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .progress-fill.primary {
          background: #3B82F6;
        }

        .progress-fill.success {
          background: #10B981;
        }

        /* Stock */
        .stock-content {
          display: flex;
          flex-direction: column;
        }

        .stock-table-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stock-value.success {
          color: #059669;
          font-weight: 600;
        }

        .stock-value.warning {
          color: #D97706;
          font-weight: 600;
        }

        .stock-value.danger {
          color: #DC2626;
          font-weight: 600;
        }

        .threshold {
          color: #6b7280;
        }

        /* États vides */
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #6b7280;
        }

        .empty-state p {
          margin-top: 1rem;
          margin-bottom: 0;
        }

        /* Responsivité */
        @media (max-width: 1024px) {
          .charts-row {
            grid-template-columns: 1fr;
          }

          .tables-row {
            grid-template-columns: 1fr;
          }

          .products-grid {
            grid-template-columns: 1fr;
          }

          .alerts-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .reporting-page {
            padding: 0.5rem;
          }

          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
          }

          .team-stats {
            grid-template-columns: 1fr;
          }

          .chart-content {
            padding: 1rem;
          }

          .table-content {
            overflow-x: auto;
          }

          table {
            min-width: 500px;
          }

          .tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .tab {
            white-space: nowrap;
            flex-shrink: 0;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .alerts-grid {
            grid-template-columns: 1fr;
          }

          .team-grid {
            grid-template-columns: 1fr;
          }

          button {
            padding: 0.625rem 1rem;
          }

          .stat-value {
            font-size: 1.125rem;
          }

          .stat-icon {
            width: 36px;
            height: 36px;
          }
        }
      `}</style>

      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <h3>Analytics & Reporting</h3>
          <p className="page-subtitle">Analyse détaillée des performances de votre boutique</p>
        </div>
        <div className="page-actions">
          <button className="btn-outline-primary" onClick={loadReportData} disabled={loading}>
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button className="btn-primary">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Alertes */}
      {error && (
        <div className="alert alert-danger">
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Contenu principal */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Chargement des données...</p>
        </div>
      ) : (
        <>
          {/* Onglets */}
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <TrendingUp size={16} />
              Dashboard
            </button>
            <button 
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              <Package size={16} />
              Produits ({reportData.topProducts.length})
            </button>
            <button 
              className={`tab ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <Users size={16} />
              Équipe ({reportData.teamPerformance.length})
            </button>
            <button 
              className={`tab ${activeTab === 'stock' ? 'active' : ''}`}
              onClick={() => setActiveTab('stock')}
            >
              <AlertTriangle size={16} />
              Stock
              {reportData.stockAlerts.length > 0 && (
                <span className="tab-badge">{reportData.stockAlerts.length}</span>
              )}
            </button>
          </div>

          {/* Contenu des onglets */}
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'products' && <ProductsTab />}
          {activeTab === 'team' && <TeamTab />}
          {activeTab === 'stock' && <StockTab />}
        </>
      )}
    </div>
  );
};

export default ReportingPage; 